/**
 * 巴西利亚时区工具（基于 moment-timezone）
 *
 * 全项目时间统一使用巴西利亚时区 America/Sao_Paulo (UTC-3)。
 * 巴西自 2019 年起不再实行夏令时，该时区全年固定 UTC-3。
 *
 * 约定：后端存储/接口收发的日期字符串均为巴西利亚本地墙钟时间
 * （如 "2024-01-15"、"2024-01-15 10:30:00"），无时区后缀时一律按 BRT 解释。
 */
import moment from 'moment-timezone'

export const BRT_TZ = 'America/Sao_Paulo'

// 设置全局默认时区：调用都按巴西利亚处理
moment.tz.setDefault(BRT_TZ)

/**
 * 将任意输入解析为巴西利亚时刻。
 * - Date 对象：保留同一 UTC 瞬时，仅以 BRT 表示
 * - 8 位 yyyyMMdd：按 BRT 本地解释
 * - 其它字符串：无时区后缀按 BRT 本地墙钟解释；带 Z/offset 的按瞬时解析后转 BRT 展示
 * @param {Date|string|number} date
 * @returns {moment.Moment|null}
 */
export function brtMoment(date) {
  if (date == null || date === '') return null
  if (date instanceof Date) {
    const m = moment(date).tz(BRT_TZ)
    return m.isValid() ? m : null
  }
  const str = String(date).replace('T', ' ').trim()
  if (/^\d{8}$/.test(str)) {
    const m = moment.tz(str, 'YYYYMMDD', BRT_TZ)
    return m.isValid() ? m : null
  }
  const m = moment.tz(str, BRT_TZ)
  return m.isValid() ? m : null
}

/**
 * 当前巴西利亚时间，格式 YYYY-MM-DD HH:mm:ss
 */
export function getBrtNow() {
  return moment().tz(BRT_TZ).format('YYYY-MM-DD HH:mm:ss')
}

/**
 * 转 ISO 风格字符串 YYYY-MM-DD HH:mm:ss（BRT）
 */
export function toBrtISOString(date) {
  const m = brtMoment(date)
  return m ? m.format('YYYY-MM-DD HH:mm:ss') : ''
}

/**
 * 转日期字符串 YYYY-MM-DD（BRT）
 */
export function toBrtDate(date) {
  const m = brtMoment(date)
  return m ? m.format('YYYY-MM-DD') : ''
}

/**
 * 转日期时间字符串 YYYY-MM-DD HH:mm（BRT）
 */
export function toBrtDateTime(date) {
  const m = brtMoment(date)
  return m ? m.format('YYYY-MM-DD HH:mm') : ''
}

/**
 * 计算两个日期之间的整天数（d2 - d1），按 BRT 墙钟差值取整
 */
export function daysBetween(date1, date2) {
  const m1 = brtMoment(date1)
  const m2 = brtMoment(date2)
  if (!m1 || !m2) return 0
  return Math.round(m2.diff(m1, 'days', true))
}

/**
 * 巴西利亚当前时间的展示串 DD/MM/YYYY HH:mm (BRT)
 */
export function getBrtDisplay() {
  return moment().tz(BRT_TZ).format('MM/DD/YYYY HH:mm (BRT)')
}

/**
 * 格式化为 DD/MM/YYYY（BRT）
 */
export function formatBrtDate(dateStr) {
  if (!dateStr && dateStr !== 0) return '-'
  const m = brtMoment(dateStr)
  return m ? m.format('MM/DD/YYYY') : (dateStr || '-')
}

/**
 * 格式化为 MM/DD/YYYY HH:mm（BRT）
 */
export function formatBrtDateTime(dateStr) {
  if (!dateStr && dateStr !== 0) return '-'
  const m = brtMoment(dateStr)
  return m ? m.format('MM/DD/YYYY HH:mm') : (dateStr || '-')
}

/**
 * 接口要求 GMT-3 时区的 MM/dd/yyyy 格式
 */
export function formatBrtApiDate(date) {
  if (!date && date !== 0) return ''
  const m = brtMoment(date)
  return m ? m.format('MM/DD/YYYY') : ''
}

/**
 * 返回一个"本地字段=巴西利亚墙钟"的 Date 对象。
 * 注意：该对象的 UTC 瞬时是被偏移过的，仅供 .getDate()/.getMonth()/.getFullYear()
 * 等本地 getter 读取 BRT 墙钟字段使用，不要当作真实瞬时再二次转换。
 */
export function getBrtCurrentDate() {
  const m = moment().tz(BRT_TZ)
  return new Date(
    m.year(), m.month(), m.date(),
    m.hour(), m.minute(), m.second(), m.millisecond()
  )
}

/**
 * 当前的巴西利亚日期字符串 YYYY-MM-DD
 */
export function getBrtDateString() {
  return toBrtDate(new Date())
}
